/**
 * Drawer Builder — onboarding funnel (Phase 3).
 *
 * A Forage-style paged wizard shown before the builder: size -> room ->
 * use-case -> finish(photo stub). Seeds the builder (drawer dimensions +
 * module pre-filter), writes a shareable size hash, then reveals the builder.
 *
 * Deliberately framework-agnostic and jQuery-free: a small state machine plus
 * a thin DOM adapter, so the logic (steps, validation, analytics schema,
 * hand-off) can later be lifted into a React-Native screen with only the view
 * rewritten. jQuery appears nowhere here; we interop with the legacy builder
 * only through public seams (#changeDimension click, window.MD_FilterStore,
 * the dimension inputs, the URL hash).
 *
 * Fail-open: the snippet renders hidden; this script reveals it only when it
 * decides to. If the script never runs, the builder is never blocked.
 */
(function () {
  'use strict';

  function mdT(key, variables) {
    if (window.FoxTheme && FoxTheme.i18n && typeof FoxTheme.i18n.t === 'function') {
      return FoxTheme.i18n.t(key, variables);
    }
    return key;
  }

  var MOTION = (window.MD_TOKENS && window.MD_TOKENS.motion) || {};
  var DUR_BASE = MOTION.durBaseMs || 300;
  var DONE_KEY = 'db_funnel_done';
  var ROOM_KEY = 'db_funnel_room';
  var SIZE_DEFAULT = { length: 413, width: 400 }; // mirrors legacy-inline defaults
  var HEIGHT_DEFAULT_MM = 45;
  var SIZE_MIN = 1;
  var SIZE_MAX = 2000;
  var GRID_UNIT_MM = (window.MD_CONST && window.MD_CONST.GRID_UNIT_MM) || 29.5;
  var TTS_FEATURE = (window.MD_FEATURES && window.MD_FEATURES.true_to_size) || {};
  var TTS_CLEARANCE_MM = Number(TTS_FEATURE.clearanceMM) || 1;
  var TTS_GAP_THRESHOLD_MM = Number(TTS_FEATURE.gapThresholdMM) || 2;
  var TTS_MAX_DIMENSION_MM = (window.MD_CONST && window.MD_CONST.TTS_MAX_DIMENSION_MM) || 1416;
  var TTS_PANEL_MAX_CELLS = (window.MD_CONST && window.MD_CONST.TTS_PANEL_MAX_CELLS) || 7;
  var TTS_MAX_PANELS = (window.MD_CONST && window.MD_CONST.TTS_MAX_PANELS) || 30;

  function ttsEligibilityForDrawer(length, width) {
    if (typeof window.mdTtsEligibility === 'function') {
      return window.mdTtsEligibility(length, width, TTS_GAP_THRESHOLD_MM);
    }
    if (!Number.isFinite(length) || !Number.isFinite(width) || length < GRID_UNIT_MM || width < GRID_UNIT_MM) {
      return { eligible: false, reason: 'minimum_grid' };
    }
    if (length > TTS_MAX_DIMENSION_MM || width > TTS_MAX_DIMENSION_MM) {
      return { eligible: false, reason: 'max_dimension' };
    }
    var rows = Math.floor(length / GRID_UNIT_MM);
    var cols = Math.floor(width / GRID_UNIT_MM);
    if (Math.ceil(rows / TTS_PANEL_MAX_CELLS) * Math.ceil(cols / TTS_PANEL_MAX_CELLS) > TTS_MAX_PANELS) {
      return { eligible: false, reason: 'max_panels' };
    }
    var gapLength = length - rows * GRID_UNIT_MM;
    var gapWidth = width - cols * GRID_UNIT_MM;
    var eligible = Math.max(gapLength, gapWidth) > TTS_GAP_THRESHOLD_MM;
    return {
      eligible: eligible,
      reason: eligible ? null : 'gap_threshold'
    };
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function parseDimension(v) {
    var n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  }

  function sessionDone() {
    try { return window.sessionStorage.getItem(DONE_KEY) === '1'; } catch (e) { return false; }
  }
  function markDone() {
    try { window.sessionStorage.setItem(DONE_KEY, '1'); } catch (e) { /* private mode */ }
  }
  function hasBuildHash() {
    return (window.location.hash || '').replace('#', '').trim().length > 0;
  }

  function readSavedRoom() {
    try { return (window.sessionStorage.getItem(ROOM_KEY) || '').toLowerCase(); } catch (e) { return ''; }
  }

  // Hand the chosen room to the builder's product filter. This is a hidden
  // room pre-filter (see applyFiltersAndPaginate in legacy-inline) — no chip,
  // it just narrows the catalogue to the room. Fail-open downstream, so it's a
  // no-op for Kitchen today and becomes a real filter once Bathroom/Office
  // modules ship tagged with their room. Persisted for the session so a reload
  // mid-design keeps the room. Pass reapply=true once products are on the page.
  function applyRoomValue(room, reapply) {
    room = (room || '').toString().trim().toLowerCase();
    if (!room) return;
    try { window.MD_BuilderRoom = room; } catch (e) {}
    try { document.body.setAttribute('data-db-active-room', room); } catch (e) {}
    try { window.sessionStorage.setItem(ROOM_KEY, room); } catch (e) {}
    if (reapply) {
      try { window.dispatchEvent(new CustomEvent('db:products-rendered')); } catch (e) {}
    }
  }

  // dataLayer events — see docs/analytics-events.md. Transport-agnostic schema:
  // event name + flat params, so a future RN app emits the same via the GA4 SDK.
  function pushDL(event, extra) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      if (extra) { for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k]; } }
      window.dataLayer.push(payload);
    } catch (e) { /* never break the flow for analytics */ }
  }

  function titleCase(s) {
    return String(s || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  onReady(function () {
    // The size hand-off needs the delayed core's Apply handler to be bound.
    if (window.MD_CATALOGUE_READY) window.MD_CATALOGUE_READY.then(init);
    else init();
  });

  function init() {
    var root = document.getElementById('db-funnel');
    if (!root) return;

    var els = {
      sheet: root.querySelector('[data-db-funnel-sheet]'),
      head: root.querySelector('.db-funnel__head'),
      track: root.querySelector('[data-db-funnel-track]'),
      progress: root.querySelector('[data-db-funnel-progress]'),
      progressBar: root.querySelector('.db-funnel__progress'),
      skip: root.querySelector('[data-db-funnel-skip]'),
      prev: root.querySelector('[data-db-funnel-prev]'),
      next: root.querySelector('[data-db-funnel-next]'),
      length: root.querySelector('[data-db-funnel-length]'),
      width: root.querySelector('[data-db-funnel-width]'),
      sizeError: root.querySelector('[data-db-funnel-size-error]'),
      sizeHint: root.querySelector('[data-db-funnel-size-hint]'),
      sizeHintText: root.querySelector('[data-db-funnel-size-hint-text]'),
      sizeHintApply: root.querySelector('[data-db-funnel-size-hint-apply]'),
      fitNote: root.querySelector('[data-db-fit-note]'),
      standardMeasure: root.querySelector('[data-db-fit-standard-measure]'),
      trueToSizeMeasure: root.querySelector('[data-db-fit-tts-measure]'),
      usecases: root.querySelector('[data-db-funnel-usecases]'),
    };
    var panels = Array.prototype.slice.call(root.querySelectorAll('.db-funnel__panel'));
    if (!panels.length || !els.track) return;

    // Active step order. The "what will you store?" use-case step was removed
    // (it duplicated the room step and added friction); module pre-filtering is
    // handled by the catalogue filters instead.
    var STEPS = ['size'];
    if (root.querySelector('[data-db-step="fit"]')) STEPS.push('fit');
    STEPS.push('room', 'finish');

    // Panels are authored `hidden` (fail-open). Reveal the ones we use so the
    // flex track lays them out; drop any panel not in STEPS.
    var stepPanels = {};
    panels.forEach(function (p) {
      var name = p.getAttribute('data-db-step');
      if (STEPS.indexOf(name) === -1) { p.remove(); return; }
      p.removeAttribute('hidden');
      stepPanels[name] = p;
    });

    // Build the pill page control — one pip per step (filled = done/current).
    els.pips = [];
    if (els.progress) {
      els.progress.innerHTML = '';
      STEPS.forEach(function () {
        var pip = document.createElement('span');
        pip.className = 'db-funnel__pip';
        els.progress.appendChild(pip);
        els.pips.push(pip);
      });
    }

    var state = {
      index: 0,
      size: { length: SIZE_DEFAULT.length, width: SIZE_DEFAULT.width },
      room: 'Kitchen',
      fit: 'true-to-size',
      fitChoiceSource: 'default',
      fitEligible: false,
      useCases: [],
      sizeApplied: false,
    };

    // ---- navigation ----------------------------------------------------
    function currentStep() { return STEPS[state.index]; }

    function publishConfiguration() {
      window.dispatchEvent(new CustomEvent('db:onboarding-config', { detail: {
        length: state.size.length,
        width: state.size.width,
        fit: state.fitEligible && state.fit === 'true-to-size' ? 'true-to-size' : 'standard'
      } }));
    }

    function render() {
      var pct = Math.round(((state.index + 1) / STEPS.length) * 100);
      els.track.style.transform = 'translateX(' + (-state.index * 100) + '%)';
      els.pips.forEach(function (pip, i) { pip.classList.toggle('is-filled', i <= state.index); });
      if (els.progressBar) els.progressBar.setAttribute('aria-valuenow', String(pct));
      els.prev.hidden = state.index === 0;
      els.next.textContent = state.index === STEPS.length - 1
        ? mdT('modu.onboarding.start_building')
        : mdT('modu.onboarding.next');
      if (currentStep() === 'fit') updateFitChoice();
      root.setAttribute('data-db-active-step', currentStep());
      Object.keys(stepPanels).forEach(function (name) {
        var selected = name === currentStep();
        stepPanels[name].classList.toggle('is-current', selected);
        stepPanels[name].setAttribute('aria-hidden', selected ? 'false' : 'true');
        stepPanels[name].toggleAttribute('inert', !selected);
      });
      window.dispatchEvent(new CustomEvent('db:onboarding-step', { detail: { step: currentStep() } }));
    }

    function goTo(index, opts) {
      index = Math.max(0, Math.min(STEPS.length - 1, index));
      if (index === state.index) { render(); return; }
      state.index = index;
      render();
      if (stepPanels[currentStep()]) stepPanels[currentStep()].scrollTop = 0;
      pushDL('onboarding_step_view', { step: index + 1, step_name: currentStep() });
      if (!(opts && opts.noFocus)) focusStep();
    }

    function focusStep() {
      var step = currentStep();
      // Intentionally do NOT auto-focus the size inputs: auto-focusing a number
      // field pops the mobile keypad before the user asks for it. The keypad now
      // appears only when the user taps a measurement field.
      if (step === 'size') return;
      var target = stepPanels[step] && stepPanels[step].querySelector('button:not([disabled]), [tabindex]');
      if (target && typeof target.focus === 'function') {
        try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      }
    }

    function validateStep(step) {
      if (step === 'size') {
        var L = parseDimension(els.length.value);
        var W = parseDimension(els.width.value);
        var ok = L >= SIZE_MIN && L <= SIZE_MAX && W >= SIZE_MIN && W <= SIZE_MAX;
        els.sizeError.hidden = ok;
        if (ok) { state.size.length = L; state.size.width = W; }
        return ok;
      }
      // room is pre-selected (Kitchen); use-case + finish are always valid.
      return true;
    }

    // Size hint under the two fields. They are millimetres and take any number
    // from 1, so a size typed in centimetres (47 × 50) passes validation and
    // seeds a drawer no module fits. Same rule and wording as the builder's top
    // bar (window.mdSizeUnits, assets/md-size-units.js); without that file there
    // is no hint. It never blocks Next: the visitor can keep what they typed.
    function readSizeHint() {
      var units = window.mdSizeUnits;
      if (!units || !els.sizeHint || !els.sizeHintText || !els.sizeHintApply) return null;
      var found = units.check({ L: units.parse(els.length.value), W: units.parse(els.width.value) });
      return found ? { units: units, found: found } : null;
    }

    function updateSizeHint() {
      if (!els.sizeHint) return;
      var hint = readSizeHint();
      if (!hint) { els.sizeHint.hidden = true; return; }
      var message = mdT('modu.builder_js.size_hint_small');
      var action = '';
      if (hint.found.kind === 'cm') {
        var size = hint.units.label(hint.found.size);
        message = hint.units.noBreak(mdT('modu.builder_js.size_hint_cm', { size: size }));
        action = hint.units.noBreak(mdT('modu.builder_js.size_hint_cm_apply', { size: size }));
      }
      els.sizeHint.hidden = false;
      // Written only when it changed: the text is a live region.
      if (els.sizeHintText.textContent !== message) els.sizeHintText.textContent = message;
      if (els.sizeHintApply.textContent !== action) els.sizeHintApply.textContent = action;
      els.sizeHintApply.hidden = !action;
    }

    // ---- builder hand-off ---------------------------------------------
    function applySize() {
      var lengthInput = document.getElementById('length') || document.querySelector('input[name="length"]');
      var widthInput = document.getElementById('width') || document.querySelector('input[name="width"]');
      var heightInput = document.getElementById('db-module-height');
      var apply = document.getElementById('changeDimension');
      if (!lengthInput || !widthInput) return;
      // Set the .value (legacy reads it via jQuery .val()) and fire input so
      // any listeners stay in sync, then trigger the existing Apply handler.
      lengthInput.value = String(state.size.length);
      widthInput.value = String(state.size.width);
      if (heightInput && heightInput.getAttribute('data-confirmed-height') == null) {
        heightInput.value = String(HEIGHT_DEFAULT_MM);
      }
      lengthInput.dispatchEvent(new Event('input', { bubbles: true }));
      widthInput.dispatchEvent(new Event('input', { bubbles: true }));
      if (apply) apply.click(); // delegated jQuery handler runs updateGridInfo()
      state.sizeApplied = true;
      updateSizeChip();
      updateFitChoice();
    }

    function formatMM(value) {
      var rounded = Math.round(value * 10) / 10;
      return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1);
    }

    function fitAxis(mm) {
      var grid = GRID_UNIT_MM;
      var standard = Math.floor(mm / grid) * grid;
      var gap = mm - standard;
      return {
        standard: standard,
        fitted:
          gap > TTS_GAP_THRESHOLD_MM
            ? mm - TTS_CLEARANCE_MM
            : standard,
        gap: gap,
      };
    }

    function updateFitChoice() {
      var fitPanel = stepPanels.fit;
      if (!fitPanel) return;
      var length = fitAxis(state.size.length);
      var width = fitAxis(state.size.width);
      var eligibility = ttsEligibilityForDrawer(state.size.length, state.size.width);
      state.fitEligible = eligibility.eligible;
      var trueCard = fitPanel.querySelector('[data-db-fit="true-to-size"]');
      if (trueCard) {
        trueCard.disabled = !state.fitEligible;
        trueCard.setAttribute('aria-disabled', state.fitEligible ? 'false' : 'true');
        trueCard.classList.toggle('is-disabled', !state.fitEligible);
      }
      var effectiveFit = state.fitEligible ? state.fit : 'standard';
      fitPanel.querySelectorAll('[data-db-fit]').forEach(function (card) {
        var selected = card.getAttribute('data-db-fit') === effectiveFit;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      if (els.standardMeasure) {
        els.standardMeasure.textContent = mdT('modu.onboarding.made_footprint', {
          dimensions: formatMM(length.standard) + ' × ' + formatMM(width.standard),
        });
      }
      if (els.trueToSizeMeasure) {
        els.trueToSizeMeasure.textContent = state.fitEligible
          ? mdT('modu.onboarding.made_footprint', {
              dimensions: formatMM(length.fitted) + ' × ' + formatMM(width.fitted),
            })
          : eligibility.reason === 'minimum_grid'
            ? mdT('modu.onboarding.minimum_grid')
            : eligibility.reason === 'max_dimension'
              ? mdT('modu.onboarding.max_dimension')
              : eligibility.reason === 'max_panels'
                ? mdT('modu.onboarding.max_panels')
                : mdT('modu.onboarding.already_grid');
      }
      if (els.fitNote) {
        els.fitNote.textContent = effectiveFit === 'true-to-size'
          ? mdT('modu.onboarding.fitted_note')
          : mdT('modu.onboarding.standard_note');
      }
    }

    function applyFit() {
      var selected = state.fit === 'true-to-size' && state.fitEligible;
      var toggle = document.getElementById('db-true-to-size');
      if (toggle) {
        if (typeof window.MD_setFitPreference === 'function') {
          window.MD_setFitPreference(state.fit, state.fitChoiceSource);
        }
        toggle.checked = selected;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
        document.body.setAttribute('data-fit-choice-source', state.fitEligible ? state.fitChoiceSource : 'unavailable');
      }
    }

    function applyUseCases() {
      if (!state.useCases.length) return;
      var store = window.MD_FilterStore;
      state.useCases.forEach(function (value) {
        if (store && typeof store.toggle === 'function') {
          store.toggle('type', value, true);
        } else {
          // Fallback: tick the legacy Type checkbox + dispatch change so
          // applyFiltersAndPaginate runs (same mechanism MD_FilterStore uses).
          var container = document.querySelector('.filter-tags-checkbox-3');
          if (!container) return;
          var inputs = container.querySelectorAll('input[type="checkbox"]');
          for (var i = 0; i < inputs.length; i += 1) {
            if ((inputs[i].value || '').trim().toLowerCase() === value && !inputs[i].checked) {
              inputs[i].checked = true;
              inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
              break;
            }
          }
        }
      });
    }

    function writeSizeHash() {
      // Shareable size-only hash (modules append later via createURL on save).
      try {
        var h = '#' + state.size.length + 'x' + state.size.width;
        if (document.body.getAttribute('data-true-to-size') === 'yes') h += '&tts=1';
        window.history.replaceState(null, '', window.location.pathname + window.location.search + h);
      } catch (e) { /* ignore */ }
    }

    // ---- terminal actions ---------------------------------------------
    function complete(keepLayout) {
      if (!keepLayout) {
        if (!state.sizeApplied) applySize();
        applyFit();
        writeSizeHash();
      }
      applyUseCases();
      applyRoomValue(state.room, true); // narrow the catalogue to the chosen room
      markDone();
      window.mdHaptic && window.mdHaptic(15);
      pushDL('onboarding_funnel_complete', {
        onboarding_room: state.room,
        onboarding_use_cases: state.useCases.join(',') || '(none)',
        onboarding_drawer_length: state.size.length,
        onboarding_drawer_width: state.size.width,
        onboarding_drawer_height: HEIGHT_DEFAULT_MM,
        onboarding_fit: state.fitEligible ? state.fit : 'standard',
      });
      hide(keepLayout ? 'layout' : 'manual');
    }

    function skip() {
      markDone();
      pushDL('onboarding_funnel_skip', { step: state.index + 1, step_name: currentStep() });
      hide('skip');
    }

    function show(reopen) {
      window.dispatchEvent(new CustomEvent('db:onboarding-opened'));
      root.removeAttribute('hidden');
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('db-funnel-open');
      // Prefill the funnel inputs from current state (chip reopen).
      els.length.value = String(state.size.length);
      els.width.value = String(state.size.width);
      updateSizeHint();
      state.fit = document.body.getAttribute('data-fit-preference') || 'true-to-size';
      state.fitChoiceSource = document.body.getAttribute('data-fit-preference-source') || 'default';
      updateFitChoice();
      if (stepPanels.size) stepPanels.size.scrollTop = 0;
      goTo(0, { noFocus: true });
      pushDL(reopen ? 'onboarding_funnel_reopen' : 'onboarding_funnel_start', {
        funnel_steps: STEPS.length,
      });
      // Defer focus until after the reveal transition starts.
      window.setTimeout(focusStep, 50);
    }

    function hide(reason) {
      root.setAttribute('hidden', '');
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('db-funnel-open');
      window.dispatchEvent(new CustomEvent('db:onboarding-dismissed', { detail: { reason: reason || 'skip' } }));
    }

    // ---- wiring --------------------------------------------------------
    els.next.addEventListener('click', function () {
      var step = currentStep();
      if (!validateStep(step)) { window.mdHaptic && window.mdHaptic([10, 40, 10]); return; }
      if (step === 'size') { applySize(); publishConfiguration(); }
      pushDL('onboarding_step_complete', { step: state.index + 1, step_name: step });
      if (state.index === STEPS.length - 1) complete();
      else goTo(state.index + 1);
    });

    els.prev.addEventListener('click', function () { goTo(state.index - 1); });
    els.skip.addEventListener('click', skip);

    // Fit selection. True to Size is available only when either axis has more
    // than 2mm of raw grid remainder.
    var fitPanel = stepPanels.fit;
    if (fitPanel) {
      fitPanel.querySelectorAll('[data-db-fit]').forEach(function (card) {
        card.addEventListener('click', function () {
          if (card.disabled || card.classList.contains('is-disabled')) return;
          state.fit = card.getAttribute('data-db-fit');
          state.fitChoiceSource = 'explicit';
          updateFitChoice();
          applyFit(); // Keep the selected card even if the customer then skips.
          if (state.sizeApplied) publishConfiguration();
          window.mdHaptic && window.mdHaptic(8);
        });
      });
      updateFitChoice();
    }

    // Room selection (single-select radiogroup; only enabled cards).
    var roomPanel = stepPanels.room;
    if (roomPanel) {
      roomPanel.querySelectorAll('[data-db-room]').forEach(function (card) {
        card.addEventListener('click', function () {
          if (card.disabled || card.classList.contains('is-disabled')) return;
          state.room = card.getAttribute('data-db-room');
          roomPanel.querySelectorAll('[data-db-room]').forEach(function (c) {
            var sel = c === card;
            c.classList.toggle('is-selected', sel);
            c.setAttribute('aria-checked', sel ? 'true' : 'false');
          });
          window.mdHaptic && window.mdHaptic(8);
        });
      });
    }

    // Use-case chips (multi-select).
    if (els.usecases) {
      els.usecases.addEventListener('click', function (e) {
        var chip = e.target.closest('.db-funnel__chip');
        if (!chip) return;
        var value = chip.getAttribute('data-value');
        var on = chip.classList.toggle('is-selected');
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        var i = state.useCases.indexOf(value);
        if (on && i === -1) state.useCases.push(value);
        else if (!on && i !== -1) state.useCases.splice(i, 1);
        window.mdHaptic && window.mdHaptic(8);
      });
    }

    // Clear the size error as the user corrects it.
    [els.length, els.width].forEach(function (inp) {
      inp.addEventListener('input', function () { els.sizeError.hidden = true; updateSizeHint(); });
    });

    // The hint's button writes the millimetre value into the two fields. The
    // focus moves to Next, the step's action: a button, so a phone keeps its
    // keypad closed.
    if (els.sizeHintApply) {
      els.sizeHintApply.addEventListener('click', function () {
        var hint = readSizeHint();
        if (hint && hint.found.kind === 'cm') {
          els.length.value = String(hint.found.size.L);
          els.width.value = String(hint.found.size.W);
          els.sizeError.hidden = true;
          window.mdHaptic && window.mdHaptic(8);
        }
        updateSizeHint();
        if (els.next && typeof els.next.focus === 'function') {
          try { els.next.focus({ preventScroll: true }); } catch (e) { els.next.focus(); }
        }
      });
    }

    setupSwipe(root, els, state, STEPS, function next() { els.next.click(); }, goTo, validateStep, currentStep);

    // Swipe the grab handle / header downward to dismiss (native bottom sheet).
    setupSheetDismiss(els.sheet, [els.sheet.querySelector('.db-funnel__grab'), els.head], skip);

    // Keep the funnel's size state in sync with manual L/W edits in the topbar.
    wireSizeChip(state, show, updateSizeChip);
    updateSizeChip();

    // body flag lets the CSS collapse the mobile dimension inputs into the
    // chip ONLY when this script is live (otherwise mobile keeps the inputs).
    document.body.classList.add('db-funnel-ready');

    // Restore a room chosen earlier this session so a reload keeps the
    // catalogue narrowed. Reapply once (cheap) so the room takes effect
    // regardless of whether the builder's initial product render ran before
    // or after this script registered MD_BuilderRoom.
    var savedRoom = readSavedRoom();
    if (savedRoom) applyRoomValue(savedRoom, true);

    render();

    // ---- gating: decide whether to auto-show ---------------------------
    if (!hasBuildHash() && !sessionDone()) {
      show(false);
    }

    // expose a tiny API for the chip / debugging.
    window.MD_Funnel = {
      open: function () { show(true); },
      isDone: sessionDone,
      completeWithLayout: function () { complete(true); }
    };

    function updateSizeChip() {
      var label = document.querySelector('[data-db-size-chip-label]');
      if (label) label.textContent = state.size.length + ' × ' + state.size.width + ' mm';
    }
  }

  // Build use-case chips from the Type filter checkboxes; returns the values.
  function buildUseCases(container) {
    if (!container) return [];
    var wrappers = document.querySelectorAll('.filter-tags-checkbox-3 .checkbox-wrapper');
    var values = [];
    var frag = document.createDocumentFragment();
    wrappers.forEach(function (w) {
      var cb = w.querySelector('input[type="checkbox"]');
      if (!cb) return;
      var value = (cb.value || '').trim().toLowerCase();
      if (!value || values.indexOf(value) !== -1) return;
      values.push(value);
      var labelEl = w.querySelector('.db-filter-option-label');
      var label = labelEl ? labelEl.textContent.trim() : value;
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'db-funnel__chip db-chip';
      chip.setAttribute('data-value', value);
      chip.setAttribute('aria-pressed', 'false');
      chip.textContent = titleCase(label);
      frag.appendChild(chip);
    });
    container.appendChild(frag);
    return values;
  }

  // Lightweight horizontal swipe over the paged track (mirrors the bottom
  // sheet's feel). Forward swipes are gated on step validation; back is free.
  function setupSwipe(root, els, state, STEPS, triggerNext, goTo, validateStep, currentStep) {
    var track = els.track;
    var startX = 0, startY = 0, dx = 0, dragging = false, locked = false, width = 1;

    function onStart(e) {
      if (e.target.closest('input, button, .db-funnel__chip, .db-funnel__card, .db-funnel__photo')) return;
      var t = e.touches ? e.touches[0] : e;
      startX = t.clientX; startY = t.clientY; dx = 0; dragging = true; locked = false;
      width = root.getBoundingClientRect().width || 1;
      track.style.transition = 'none';
    }
    function onMove(e) {
      if (!dragging) return;
      var t = e.touches ? e.touches[0] : e;
      var mx = t.clientX - startX, my = t.clientY - startY;
      if (!locked) {
        if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
        if (Math.abs(my) > Math.abs(mx)) { dragging = false; return; } // vertical scroll
        locked = true;
      }
      dx = mx;
      // Resist when there is nowhere to go (first/last) or forward is blocked.
      var atStart = state.index === 0 && dx > 0;
      var atEnd = state.index === STEPS.length - 1 && dx < 0;
      var forwardBlocked = dx < 0 && !validateStep(currentStep());
      if (atStart || atEnd || forwardBlocked) dx *= 0.3;
      track.style.transform = 'translateX(' + (-state.index * 100 + (dx / width) * 100) + '%)';
      if (e.cancelable) e.preventDefault();
    }
    function onEnd() {
      if (!dragging) { return; }
      dragging = false;
      track.style.transition = '';
      var threshold = width * 0.25;
      if (dx <= -threshold) {
        // Forward: snap the transform back to the current step first so the
        // track is never stranded mid-drag, then let Next validate + advance
        // (a no-op if the step is invalid — the snap-back is what remains).
        goTo(state.index, { noFocus: true });
        triggerNext();
      } else if (dx >= threshold && state.index > 0) {
        goTo(state.index - 1);
      } else {
        goTo(state.index, { noFocus: true }); // snap back
      }
      dx = 0;
    }

    track.addEventListener('touchstart', onStart, { passive: true });
    track.addEventListener('touchmove', onMove, { passive: false });
    track.addEventListener('touchend', onEnd);
    track.addEventListener('touchcancel', onEnd);
  }

  // Vertical swipe-to-dismiss on the sheet header. Drags the whole sheet with
  // the finger; releasing past ~25% of its height dismisses (same as Skip).
  function setupSheetDismiss(sheet, handles, onDismiss) {
    if (!sheet) return;
    handles = (handles || []).filter(Boolean);
    if (!handles.length) return;
    var startY = 0, dy = 0, dragging = false, h = 1;
    function start(e) {
      if (e.target.closest('button, input')) return; // let Skip etc. work
      var t = e.touches ? e.touches[0] : e;
      startY = t.clientY; dy = 0; dragging = true;
      h = sheet.getBoundingClientRect().height || 1;
      sheet.style.transition = 'none';
    }
    function move(e) {
      if (!dragging) return;
      var t = e.touches ? e.touches[0] : e;
      dy = t.clientY - startY;
      if (dy < 0) dy *= 0.2; // resist upward
      sheet.style.transform = 'translateY(' + dy + 'px)';
    }
    function end() {
      if (!dragging) return;
      dragging = false;
      sheet.style.transition = '';
      sheet.style.transform = '';
      if (dy > h * 0.25) onDismiss();
      dy = 0;
    }
    handles.forEach(function (handle) {
      handle.addEventListener('touchstart', start, { passive: true });
      handle.addEventListener('touchmove', move, { passive: true });
      handle.addEventListener('touchend', end);
      handle.addEventListener('touchcancel', end);
    });
  }

  // The size chip is no longer used as a dimension control (the L/W inputs stay
  // expanded in the topbar). We only mirror manual L/W edits back into the
  // funnel's size state so a later reopen reflects the current size.
  function wireSizeChip(state, show, updateSizeChip) {
    var apply = document.getElementById('changeDimension');
    if (!apply) return;
    apply.addEventListener('click', function () {
      window.setTimeout(function () {
        var l = document.getElementById('length');
        var w = document.getElementById('width');
        if (l && w) {
          var L = parseDimension(l.value), W = parseDimension(w.value);
          if (Number.isFinite(L)) state.size.length = L;
          if (Number.isFinite(W)) state.size.width = W;
          updateSizeChip();
        }
      }, 0);
    });
  }
})();
